import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiConnectorPost } from "../../services/apiconnector";
import { endpoint } from "../../utils/APIRoutes";
import moment from "moment";

const LEVELS = Array.from({ length: 15 }, (_, i) => i + 1);
const LIMIT = 10;

const C = {
  bg:      "#020c12",
  card:    "rgba(4,16,24,0.90)",
  border:  "rgba(0,229,255,0.15)",
  cyan:    "#00e5ff",
  cyanDim: "#4dd6ea",
  text:    "#e0f7fa",
  muted:   "#90cad4",
};

export default function SubordinateData() {
  const navigate = useNavigate();
  const [level, setLevel]   = useState(1);
  const [page, setPage]     = useState(1);

  const { data: teamRes, isLoading } = useQuery({
    queryKey: ["subordinate_team", level, page],
    queryFn: () =>
      apiConnectorPost(endpoint.get_downline_team, {
        level_id: level,
        page,
        limit: LIMIT,
      }),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const result     = teamRes?.data?.result || {};
  const rows       = result?.data || [];
  const totalPages = result?.totalPage || 1;

  const fmt = (dateStr) =>
    dateStr ? moment(dateStr).format("DD MMM YYYY") : "—";

  return (
    <div style={s.root}>
      <div style={s.grid} />
      <div style={s.glowTL} />
      <div style={s.glowBR} />

      <div style={s.container}>

        {/* ── Header ── */}
        <div style={s.header}>
          <button style={s.backBtn} onClick={() => navigate("/Promotion")}>‹</button>
          <div style={s.titleWrap}>
            <span style={s.titleIcon}>📊</span>
            <h1 style={s.title}>Subordinate Data</h1>
          </div>
        </div>

        {/* ── Level selector ── */}
        <div style={s.levelWrap}>
          <p style={s.levelLabel}>SELECT LEVEL</p>
          <div style={s.levelGrid}>
            {LEVELS.map((l) => (
              <button
                key={l}
                style={{ ...s.levelBtn, ...(level === l ? s.levelBtnActive : {}) }}
                onClick={() => { setLevel(l); setPage(1); }}
              >
                L{l}
              </button>
            ))}
          </div>
        </div>

        {/* ── Table card ── */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardIcon}>👥</span>
            <span style={s.cardTitle}>Level {level} Members</span>
          </div>

          {isLoading ? (
            <div style={s.center}><div style={s.spinner} /></div>
          ) : rows.length === 0 ? (
            <div style={s.center}>
              <p style={s.empty}>No members found at Level {level}</p>
            </div>
          ) : (
            <>
              {rows.map((row, i) => (
                <div key={i} style={{ ...s.memberRow, ...(i === rows.length - 1 ? { borderBottom: "none" } : {}) }}>
                  <div style={s.memberLeft}>
                    <div style={s.avatar}>{(row.lgn_name || "?")[0].toUpperCase()}</div>
                    <div>
                      <p style={s.memberName}>{row.lgn_name || "N/A"}</p>
                      <p style={s.memberId}>{row.lgn_cust_id}</p>
                    </div>
                  </div>
                  <div style={s.memberRight}>
                    <p style={s.memberAmount}>${parseFloat(row.tr03_auto_trade_fund || 0).toFixed(2)}</p>
                    <p style={s.memberDate}>{fmt(row.tr03_reg_date)}</p>
                  </div>
                </div>
              ))}

              {/* ── Pagination ── */}
              <div style={s.pagination}>
                <button
                  style={{ ...s.pageBtn, ...(page <= 1 ? s.pageBtnDisabled : {}) }}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  ‹ Prev
                </button>
                <span style={s.pageInfo}>Page {page} / {totalPages}</span>
                <button
                  style={{ ...s.pageBtn, ...(page >= totalPages ? s.pageBtnDisabled : {}) }}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next ›
                </button>
              </div>
            </>
          )}
        </div>

      </div>

      <style>{`
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </div>
  );
}

const s = {
  root: {
    minHeight: "100vh", background: C.bg, fontFamily: "'Sora',sans-serif",
    position: "relative", overflow: "hidden", paddingBottom: 100,
  },
  grid: {
    position: "fixed", inset: 0, pointerEvents: "none",
    backgroundImage: "linear-gradient(rgba(0,229,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,229,255,0.03) 1px,transparent 1px)",
    backgroundSize: "44px 44px",
  },
  glowTL: {
    position: "fixed", top: -150, left: -100, width: 480, height: 480,
    borderRadius: "50%", pointerEvents: "none",
    background: "radial-gradient(circle,rgba(0,229,255,0.08) 0%,transparent 70%)",
  },
  glowBR: {
    position: "fixed", bottom: -100, right: -80, width: 380, height: 380,
    borderRadius: "50%", pointerEvents: "none",
    background: "radial-gradient(circle,rgba(0,180,200,0.05) 0%,transparent 70%)",
  },
  container: {
    maxWidth: 480, margin: "0 auto", padding: "24px 16px 40px",
    position: "relative", zIndex: 10,
    animation: "fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both",
  },
  header: { display: "flex", alignItems: "center", gap: 14, marginBottom: 20 },
  backBtn: {
    background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.2)",
    color: C.cyan, borderRadius: 10, width: 36, height: 36,
    fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  },
  titleWrap:  { display: "flex", alignItems: "center", gap: 10 },
  titleIcon:  { fontSize: 20 },
  title: { fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" },

  levelWrap: {
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: 18, padding: "14px 16px", marginBottom: 14,
    backdropFilter: "blur(20px)", boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  },
  levelLabel: {
    fontSize: 9, color: C.cyanDim, fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.18em", marginBottom: 10,
  },
  levelGrid: {
    display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6,
  },
  levelBtn: {
    height: 34, borderRadius: 8,
    border: "1px solid rgba(0,229,255,0.2)",
    background: "rgba(0,229,255,0.04)",
    color: "#b0dce6", fontSize: 11, fontWeight: 600, cursor: "pointer",
    transition: "all 0.2s",
  },
  levelBtnActive: {
    background: "rgba(0,229,255,0.12)",
    border: "1px solid rgba(0,229,255,0.4)",
    color: C.cyan, boxShadow: "0 0 12px rgba(0,229,255,0.1)",
  },

  card: {
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: 18, overflow: "hidden",
    backdropFilter: "blur(20px)", boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  },
  cardHeader: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "14px 18px 12px", borderBottom: `1px solid ${C.border}`,
    background: "rgba(0,229,255,0.025)",
  },
  cardIcon:  { fontSize: 16 },
  cardTitle: { fontSize: 13, fontWeight: 600, color: C.text },

  memberRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 18px", borderBottom: "1px solid rgba(0,229,255,0.05)",
  },
  memberLeft:   { display: "flex", alignItems: "center", gap: 12 },
  avatar: {
    width: 38, height: 38, borderRadius: "50%",
    background: "rgba(0,229,255,0.1)", border: "1px solid rgba(0,229,255,0.25)",
    color: C.cyan, fontSize: 15, fontWeight: 700,
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  },
  memberName: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 },
  memberId:   { fontSize: 10, color: "#90cad4", fontFamily: "'Space Mono',monospace" },
  memberRight: { textAlign: "right" },
  memberAmount: {
    fontSize: 13, fontWeight: 700, color: C.cyan,
    fontFamily: "'Space Mono',monospace", marginBottom: 2,
  },
  memberDate: { fontSize: 10, color: "#90cad4" },

  pagination: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 18px", borderTop: `1px solid ${C.border}`,
  },
  pageBtn: {
    background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.2)",
    color: C.cyan, borderRadius: 8, padding: "6px 14px",
    fontSize: 11, fontFamily: "'Space Mono',monospace", fontWeight: 700,
    cursor: "pointer",
  },
  pageBtnDisabled: { opacity: 0.3, cursor: "not-allowed" },
  pageInfo: { fontSize: 11, color: "#b0dce6", fontFamily: "'Space Mono',monospace" },

  center: {
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "40px 16px",
  },
  spinner: {
    width: 36, height: 36, borderRadius: "50%",
    border: "3px solid rgba(0,229,255,0.15)", borderTopColor: "#00e5ff",
    animation: "spin 0.75s linear infinite",
  },
  empty: { fontSize: 13, color: C.muted, textAlign: "center" },
};
